import { useQuery } from '@apollo/client';
import { GET_LAST_PROSPECT_MESSAGE, GetContactsWeightedScoresQuery } from '@zaplify/graphql';
import { RepliedBadge } from './badges/replied-badge';
import { useActions } from '../../hooks/use-actions';
import { HotConversationBadge } from './badges/hot-conversation-badge';
import { NewContactBadge } from './badges/new-contact-badge';
import { MatchBadge } from './badges/match-badge';
import { ReEngageBadge } from './badges/re-engage-badge';
import { RecommendedActionBadge } from './badges/recommended-action-badge';
import moment from 'moment';
import { Direction } from '@zaplify/campaigns';
import { AwaitingResponseBadge } from './badges/awaiting-response-badge';
import { RecommendedActionSchema } from '@andsend/intelligence-shared';
import { z } from 'zod';
import { BadgeProps } from '@shadcn/ui/components/ui/badge';

type MessageInput = GetContactsWeightedScoresQuery['contacts_weighted_scores'][number]['Messages'][number];
type RecommendedAction = z.infer<typeof RecommendedActionSchema>;

export const ActionBadge = ({
    prospectId,
    isCompleted,
    lastMessageInput,
    recommendedAction,
}: {
    prospectId: string;
    isCompleted: boolean;
    lastMessageInput?: MessageInput;
    recommendedAction?: RecommendedAction;
}) => {
    const { getBadge, getAction } = useActions();

    const scheduledAction = getAction(prospectId);
    const suggestedBadge = recommendedAction?.actionType || getBadge(prospectId);

    const { data } = useQuery(GET_LAST_PROSPECT_MESSAGE, {
        variables: { prospectId: prospectId },
        skip: !prospectId || lastMessageInput !== undefined,
    });
    const lastMessage = lastMessageInput || data?.Messages?.[0];
    const isReply = lastMessage?.direction === Direction.IN;

    let badge: React.ReactNode = null;
    if (isCompleted) {
        badge = <AwaitingResponseBadge />;
    } else if (suggestedBadge && !recommendedAction) {
        if (suggestedBadge === 'hot conversation') {
            badge = <HotConversationBadge />;
        } else if (suggestedBadge === 'matching playbook') {
            badge = <MatchBadge />;
        } else if (suggestedBadge === 'new contact') {
            badge = (
                <NewContactBadge addedAt={moment(scheduledAction.ConversationMetaData.userContactAddedAt).fromNow()} />
            );
        } else if (suggestedBadge === 'reengage') {
            badge = <ReEngageBadge />;
        }
    } else if (recommendedAction) {
        const actionTypeToText = {
            SEND_EMAIL: 'Send Email',
            SEND_LINKEDIN_MESSAGE: 'Send LinkedIn Message',
            PHONE_CALL: 'Phone Call',
            MEETING: 'Meeting',
            MAKE_INTRODUCTION: 'Make Introduction',
            SCHEDULE_CALL: 'Schedule Call',
            SCHEDULE_MEETING: 'Schedule Meeting',
            SEND_FOLLOW_UP: 'Send Follow-up',
            SHARE_CONTENT: 'Share Content',
            ESCALATE_INTERNALLY: 'Escalate Internally',
            RESEARCH_MORE: 'Research More',
            NURTURE_RELATIONSHIP: 'Nurture Relationship',
            WAIT_FOR_RESPONSE: 'Wait for Response',
            NONE: 'No Action',
        };

        const actionTypeToColor = {
            SEND_EMAIL: 'success',
            SEND_LINKEDIN_MESSAGE: 'success',
            PHONE_CALL: 'success',
            MEETING: 'success',
            MAKE_INTRODUCTION: 'brand',
            SCHEDULE_CALL: 'success',
            SCHEDULE_MEETING: 'success',
            SEND_FOLLOW_UP: 'warning',
            SHARE_CONTENT: 'brand',
            ESCALATE_INTERNALLY: 'warning',
            RESEARCH_MORE: 'brand',
            NURTURE_RELATIONSHIP: 'brand',
            WAIT_FOR_RESPONSE: 'brand',
            NONE: 'outline',
        };

        const text = actionTypeToText[recommendedAction.actionType] || recommendedAction.actionType;
        const color: BadgeProps['variant'] =
            (actionTypeToColor[recommendedAction.actionType] as BadgeProps['variant']) || 'default';

        badge = <RecommendedActionBadge text={text} color={color} />;
    } else if (isReply) {
        badge = <RepliedBadge />;
    }

    return badge;
};
